import {
  Container,
  Box,
  Header,
  Button,
  Grid,
  SpaceBetween,
  Input,
  ExpandableSection,
  FormField,
  Select,
  SelectProps,
  Tabs,
  Alert,
} from '@cloudscape-design/components';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { CreateReportResponse, ReportContactRequest } from '../../common/types';
import { createReports } from '../../common/api/reports';
import { createChainSearch, getChainAggregate, US_STATES } from '../../common/api/chains';
import { LoadingOrError } from '../../components/Loading';
import MultiAutoCompleteSearch from '../../components/MultiAutoCompleteSearch';
import { Suggestion } from '../../common/useMaps';
import { BUSINESS_TYPES } from '../../common/types';
import { getBusinessType } from '../../common/businessTypes';
import ReportCardHomeHeader from '../../components/ReportCardHomeHeader';
import { ReportProgress } from '../home';
import { PublicScorecardPreview } from '../../components/PublicScorecardPreview';

type RegionType = 'state' | 'zip' | 'city' | 'nationwide';

function SingleBusinessTab() {
  const [selectedItems, setSelectedItems] = useState<Suggestion[]>([]);
  const [reports, setReports] = useState<CreateReportResponse[]>([]);

  const createMutation = useMutation({
    mutationFn: createReports,
    onSuccess: (data) => {
      setSelectedItems([]);
      setReports(data.filter((d): d is CreateReportResponse => d !== undefined));
    },
  });

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [businessType, setBusinessType] = useState<SelectProps.Option | null>(null);

  const validPhoneNumber = useMemo(() => {
    if (!phoneNumber.trim()) return false;
    return phoneNumber.trim().length >= 10 && phoneNumber.trim().length <= 15;
  }, [phoneNumber]);

  const validEmail = useMemo(() => {
    if (!email.trim()) return false;
    return email.trim().includes('@');
  }, [email]);

  const hasContactInfo = useMemo(
    () => validEmail || validPhoneNumber,
    [validEmail, validPhoneNumber],
  );

  const invalidContact = useMemo(() => {
    if (firstName.trim() && !hasContactInfo) return true;
    if (!firstName.trim() && hasContactInfo) return true;
    return false;
  }, [firstName, hasContactInfo]);

  const anyContact = useMemo(
    () =>
      !!(firstName.trim() || lastName.trim() || email.trim() || phoneNumber.trim() || title.trim()),
    [firstName, lastName, email, phoneNumber, title],
  );

  const handleCreate = () => {
    if (createMutation.isPending) return;
    if (selectedItems.length === 0) return;
    let contact: ReportContactRequest | undefined;
    if (anyContact) {
      if (invalidContact) {
        setShowErrors(true);
        return;
      }
      contact = {
        first_name: firstName.trim(),
        last_name: lastName.trim() || undefined,
        email: email.trim() || undefined,
        phone_number: phoneNumber.trim() || undefined,
        title: title.trim() || undefined,
      };
    }
    setReports([]);
    createMutation.mutate({
      place_ids: selectedItems.map((item) => item.placeId),
      contact,
      business_type:
        businessType?.value === 'Other' ? undefined : (businessType?.value ?? undefined),
    });
  };

  useEffect(() => {
    if (selectedItems.length > 0) {
      const firstItem = selectedItems[0];
      const googleTags = firstItem.tags;
      const bt = getBusinessType(googleTags);
      setBusinessType(bt ? { label: bt, value: bt } : { label: 'Other', value: 'Other' });
    }
  }, [selectedItems]);

  return (
    <Grid gridDefinition={[{ colspan: { default: 12 } }, { colspan: { default: 12 } }]}>
      <Container
        header={
          <Header
            variant="h1"
            description="Enter a business name to create a Reputation Scorecard from its Google, Yelp and BBB reviews"
            actions={
              <Button variant="normal" href="/dashboard/scorecard">
                View Scorecards
              </Button>
            }
          >
            Create a Reputation Scorecard
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="s">
          <MultiAutoCompleteSearch
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            loading={createMutation.isPending}
          />
          <Grid gridDefinition={[{ colspan: { default: 12, xs: 6 } }]}>
            <FormField label="Business Type">
              <Select
                selectedOption={businessType}
                options={BUSINESS_TYPES}
                onChange={({ detail }) => setBusinessType(detail.selectedOption)}
                disabled={createMutation.isPending}
                placeholder="Select a business type"
              />
            </FormField>
          </Grid>
          <ExpandableSection headerText="Add contact information">
            <Grid
              gridDefinition={[
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
              ]}
            >
              <FormField
                label="First Name"
                errorText={
                  showErrors && anyContact && !firstName.trim()
                    ? 'First name is required'
                    : undefined
                }
              >
                <Input
                  value={firstName}
                  onChange={({ detail }) => setFirstName(detail.value)}
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField label="Last Name">
                <Input
                  value={lastName}
                  onChange={({ detail }) => setLastName(detail.value)}
                  placeholder="(optional)"
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField
                label="Email"
                errorText={
                  showErrors && anyContact && !hasContactInfo
                    ? 'Email or phone number is required'
                    : undefined
                }
              >
                <Input
                  value={email}
                  onChange={({ detail }) => setEmail(detail.value)}
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField
                label="Phone Number"
                errorText={
                  showErrors && anyContact && !hasContactInfo
                    ? 'Email or phone number is required'
                    : undefined
                }
              >
                <Input
                  value={phoneNumber}
                  onChange={({ detail }) => setPhoneNumber(detail.value)}
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField label="Title">
                <Input
                  value={title}
                  onChange={({ detail }) => setTitle(detail.value)}
                  placeholder="(optional)"
                  disabled={createMutation.isPending}
                />
              </FormField>
            </Grid>
          </ExpandableSection>
          <Box float="right">
            <Button
              variant="primary"
              iconName="add-plus"
              onClick={handleCreate}
              loading={createMutation.isPending}
              disabled={selectedItems.length === 0}
            >
              {selectedItems.length > 1
                ? `Create Scorecards (${selectedItems.length})`
                : 'Create Scorecard'}
            </Button>
          </Box>
          {createMutation.isError && (
            <LoadingOrError loading={createMutation.isPending} error={createMutation.error} />
          )}
        </SpaceBetween>
      </Container>
      {!createMutation.isPending && !createMutation.isSuccess && <PublicScorecardPreview />}
      {reports.length > 0 && (
        <div>
          {reports.map((report) => (
            <ReportProgress key={report.uuid} uuid={report.uuid} />
          ))}
        </div>
      )}
    </Grid>
  );
}

function ChainBulkTab({ targetChainUuid }: { targetChainUuid?: string }) {
  const navigate = useNavigate();
  const [chainName, setChainName] = useState('');
  const [regionType, setRegionType] = useState<RegionType>('state');
  const [stateCode, setStateCode] = useState<SelectProps.Option | null>({
    label: 'Texas',
    value: 'TX',
  });
  const [zipValue, setZipValue] = useState('');
  const [cityValue, setCityValue] = useState('');
  const [notes, setNotes] = useState('');
  const [businessType, setBusinessType] = useState<SelectProps.Option | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // "Add locations to an existing chain" mode: /dashboard/chains/$uuid →
  // Add locations deep-links here with ?chain=<uuid>. The search then runs
  // targeted at that chain — the backend excludes already-added locations
  // from discovery results, and selected locations land in that chain.
  const targetChainQuery = useQuery({
    queryKey: ['chain-aggregate', targetChainUuid],
    queryFn: () => getChainAggregate(targetChainUuid!),
    enabled: !!targetChainUuid,
  });
  const targetChain = targetChainUuid ? targetChainQuery.data : undefined;

  // Pre-fill the search term with the target chain's name — the usual case
  // when hunting for missed locations of an existing brand. One-shot so the
  // user can still clear/edit it (e.g. alternate spellings).
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (targetChain && !prefilled) {
      setPrefilled(true);
      setChainName((current) => current || targetChain.name);
    }
  }, [targetChain, prefilled]);

  const createMutation = useMutation({
    mutationFn: createChainSearch,
    onSuccess: (data) => {
      if (data) {
        navigate({
          to: '/dashboard/chains/searches/$uuid',
          params: { uuid: data.uuid },
        });
      }
    },
    onError: (e) => setErrorMsg(String(e)),
  });

  const regionValue = useMemo(() => {
    switch (regionType) {
      case 'state':
        return stateCode?.value ?? '';
      case 'zip':
        return zipValue.trim();
      case 'city':
        return cityValue.trim();
      case 'nationwide':
        return '';
    }
  }, [regionType, stateCode, zipValue, cityValue]);

  const canSubmit = useMemo(() => {
    if (!chainName.trim()) return false;
    if (regionType === 'nationwide') return true;
    return regionValue.length > 0;
  }, [chainName, regionType, regionValue]);

  const submit = () => {
    setErrorMsg(null);
    if (!canSubmit) return;
    createMutation.mutate({
      chain_name: chainName.trim(),
      region_type: regionType,
      region_value: regionValue,
      notes: notes.trim() || undefined,
      business_type:
        businessType?.value === 'Other' ? undefined : (businessType?.value ?? undefined),
      chain_uuid: targetChainUuid || undefined,
    });
  };

  return (
    <Container
      header={
        <Header
          variant="h1"
          description={
            targetChainUuid
              ? 'Search for locations to add to this chain. Locations already in the chain are excluded from the results automatically.'
              : 'Discover all locations of a chain in a region, then bulk-create scorecards for the ones you select. Internal only.'
          }
          actions={
            <Button
              variant="normal"
              onClick={() =>
                targetChainUuid
                  ? navigate({
                      to: '/dashboard/chains/$chainUuid',
                      params: { chainUuid: targetChainUuid },
                    })
                  : navigate({ to: '/dashboard/chains' })
              }
            >
              {targetChainUuid ? 'Back to chain' : 'View chains'}
            </Button>
          }
        >
          {targetChain ? `Add locations to ${targetChain.name}` : 'Bulk create by chain'}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {errorMsg && (
          <Alert type="error" header="Submit failed">
            {errorMsg}
          </Alert>
        )}
        {targetChainUuid && targetChainQuery.error ? (
          <Alert type="error" header="Failed to load target chain">
            {String(targetChainQuery.error)}
          </Alert>
        ) : null}
        <FormField
          label="Chain name"
          description="Brand name as it would appear in Google Maps results (e.g. 'Jiffy Lube', 'Starbucks')."
        >
          <Input
            value={chainName}
            onChange={({ detail }) => setChainName(detail.value)}
            placeholder="Jiffy Lube"
            disabled={createMutation.isPending}
          />
        </FormField>

        <FormField
          label="Region"
          description="State coverage uses a multi-metro grid (5-15 search points per state) since a single Google text search caps at 20 results. Zip and city are single-point — only useful for narrow searches."
        >
          <Select
            selectedOption={
              regionType ? { label: regionTypeLabel(regionType), value: regionType } : null
            }
            options={[
              { label: 'State (recommended)', value: 'state' },
              { label: 'ZIP code', value: 'zip' },
              { label: 'City', value: 'city' },
              { label: 'Nationwide (slow & expensive)', value: 'nationwide' },
            ]}
            onChange={({ detail }) => setRegionType(detail.selectedOption.value as RegionType)}
            disabled={createMutation.isPending}
          />
        </FormField>

        {regionType === 'state' && (
          <FormField label="State">
            <Select
              selectedOption={stateCode}
              options={US_STATES.map((s) => ({ label: `${s.name} (${s.code})`, value: s.code }))}
              onChange={({ detail }) => setStateCode(detail.selectedOption)}
              disabled={createMutation.isPending}
              filteringType="auto"
            />
          </FormField>
        )}
        {regionType === 'zip' && (
          <FormField
            label="ZIP code"
            description="5-digit US zip; runs a single 50km-radius search at that location."
          >
            <Input
              value={zipValue}
              onChange={({ detail }) => setZipValue(detail.value.trim())}
              placeholder="75201"
              inputMode="numeric"
              disabled={createMutation.isPending}
            />
          </FormField>
        )}
        {regionType === 'city' && (
          <FormField
            label="City"
            description="e.g. 'Houston, TX' or 'Manhattan, NY'. Geocoded once, then a single 50km search."
          >
            <Input
              value={cityValue}
              onChange={({ detail }) => setCityValue(detail.value)}
              placeholder="Houston, TX"
              disabled={createMutation.isPending}
            />
          </FormField>
        )}

        <Grid gridDefinition={[{ colspan: { default: 12, xs: 6 } }]}>
          <FormField
            label="Business type (optional)"
            description="Applied to every scorecard you create from this search."
          >
            <Select
              selectedOption={businessType}
              options={BUSINESS_TYPES}
              onChange={({ detail }) => setBusinessType(detail.selectedOption)}
              disabled={createMutation.isPending}
              placeholder="(none)"
            />
          </FormField>
        </Grid>

        <FormField label="Notes (optional)">
          <Input
            value={notes}
            onChange={({ detail }) => setNotes(detail.value)}
            disabled={createMutation.isPending}
            placeholder="Free-form context"
          />
        </FormField>

        <Box float="right">
          <Button
            variant="primary"
            iconName="search"
            onClick={submit}
            loading={createMutation.isPending}
            disabled={!canSubmit}
          >
            Discover locations
          </Button>
        </Box>
      </SpaceBetween>
    </Container>
  );
}

function regionTypeLabel(t: RegionType): string {
  switch (t) {
    case 'state':
      return 'State (recommended)';
    case 'zip':
      return 'ZIP code';
    case 'city':
      return 'City';
    case 'nationwide':
      return 'Nationwide (slow & expensive)';
  }
}

export default function View() {
  // Tab selection lives in the URL (`?tab=single|chain`) so "New chain
  // search" buttons can deep-link straight to the Chain (bulk) tab.
  // `?chain=<uuid>` targets the chain search at an existing chain ("Add
  // locations" from a chain detail page).
  const { tab, chain } = useSearch({ from: '/dashboard/scorecard_/create' });
  const navigate = useNavigate();

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Scorecards', href: '/dashboard/scorecard' },
        { text: 'Create', href: '/dashboard/scorecard/create' },
      ]}
      header={<ReportCardHomeHeader />}
      headerVariant="high-contrast"
      contentType="dashboard"
    >
      {/* variant="container" wraps the tab labels in their own white
          container so they don't get rendered straight onto the dark
          high-contrast page header (where the inactive tab's gray label
          becomes illegible on the dark background). */}
      <Tabs
        variant="container"
        activeTabId={tab}
        onChange={({ detail }) =>
          navigate({
            to: '/dashboard/scorecard/create',
            search: { tab: detail.activeTabId === 'chain' ? 'chain' : 'single' },
            replace: true,
          })
        }
        tabs={[
          {
            id: 'single',
            label: 'Single business',
            content: <SingleBusinessTab />,
          },
          {
            id: 'chain',
            label: 'Chain (bulk)',
            content: <ChainBulkTab targetChainUuid={chain} />,
          },
        ]}
      />
    </DashboardViewLayout>
  );
}
